import React from "react";
import { SelectProps } from "./SelectOption.types";
import styles from "./SelectOption.module.scss";
import { CICheck, CITimes, CIAngleDownSmall } from "@dua.design/icons";
import Typography from "../Typography";
import Select, {
  ClearIndicatorProps,
  DropdownIndicatorProps,
  GroupBase,
  MultiValueRemoveProps,
  OptionProps,
} from "react-select";

const SelectOption = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: SelectProps<Option, IsMulti, Group>
) => {
  return (
    <div data-testid="SelectOption">
      <Typography
        variant="label"
        token={100}
        weight="normal"
        className={styles.SelectOptionLabel}
      >
        {props.label ? props.label : null}{" "}
        {props.isRequired ? <span>*</span> : null}
      </Typography>
      <Select
        {...props}
        className={styles.ReactSelectStyle}
        unstyled
        isDisabled={props.isDisabled || props.isReadOnly}
        styles={{
          control: (base, state) => ({
            ...base,
            fontFamily: `"tt_norms_pronormal", -apple-system, BlinkMacSystemFont,
      "Segoe UI", "Roboto", sans-serif;`,
            fontWeight: 450,
            fontSize: "14px",
            lineHeight: "18px",
            backgroundColor:
              props.isDisabled || props.isReadOnly ? "#E9E9E9" : "#fff",
            maxWidth: "350px",
            minWidth: "240px",
            borderRadius: "12px",
            padding: "12px 16px",
            cursor: !state.isDisabled ? "pointer" : "inherit",
            color: "#35393D",
            border:
              !state.isFocused && !props.error
                ? "1px solid #E9E9E9"
                : props.error
                ? "1px solid red"
                : "1px solid #BCBDBE",
          }),
          menu: (base) => ({
            ...base,
            fontFamily: `"tt_norms_pronormal", -apple-system, BlinkMacSystemFont,
      "Segoe UI", "Roboto", sans-serif;`,
            fontWeight: 500,
            backgroundColor: "#fff",
            maxWidth: "350px",
            minWidth: "240px",
            borderRadius: "8px",
            padding: "4px 0",
            marginTop: "2px",
            border: "1px solid #E9E9E9",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.08)",
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: "350px",
          }),
          multiValue: (base) => ({
            ...base,
            fontFamily: `"tt_norms_pronormal", -apple-system, BlinkMacSystemFont,
      "Segoe UI", "Roboto", sans-serif;`,
            backgroundColor: "#E9E9E9",
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: "450",
            marginRight: "4px",
            padding: "2px 6px",
            borderRadius: "4px",
            color: "#35393D",
          }),
          placeholder: (base, state) => ({
            ...base,
            fontFamily: `"tt_norms_pronormal", -apple-system, BlinkMacSystemFont,
      "Segoe UI", "Roboto", sans-serif;`,
            fontSize: "14px",
            lineHeight: "18px",
            fontWeight: "450",
            color:
              state.isDisabled && !props.isReadOnly
                ? "#8F9193"
                : state.isDisabled && props.isReadOnly
                ? "#35393D"
                : "#35393D",
          }),
        }}
        hideSelectedOptions={false}
        components={{
          Option: CustomOption,
          ClearIndicator,
          DropdownIndicator,
          MultiValueRemove,
        }}
      />
    </div>
  );
};

const CustomOption = (props: OptionProps) => (
  <div {...props.innerProps} className={styles.ReactSelectSingleOption}>
    <Typography variant="p" token={100} weight="medium">
      {props.children}
    </Typography>{" "}
    {props.isSelected ? <CICheck color="#8F9193" size="18px" /> : null}
  </div>
);

const ClearIndicator = (props: ClearIndicatorProps<any, true>) => {
  const {
    children = <CITimes />,
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref} style={{ height: "14px" }}>
      <div>{children}</div>
    </div>
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps<any, true>) => {
  const {
    children = <CITimes />,
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div
      {...restInnerProps}
      ref={ref}
      style={{ marginLeft: "4px", marginTop: "2px", height: "14px" }}
    >
      <div>{children}</div>
    </div>
  );
};

const DropdownIndicator = (props: DropdownIndicatorProps<any, true>) => (
  <CIAngleDownSmall
    color={!props.isDisabled ? "#8F9193" : "#BCBDBE"}
    size="24px"
  />
);

export default SelectOption;
